// Straight-line distance is enough to rank clubs by nearness. Anything better
// means a routing API and a key, and it would not change the top of the list.
const EARTH_RADIUS_KM = 6371;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export function haversineKm(a, b) {
	const dLat = toRadians(b.lat - a.lat);
	const dLng = toRadians(b.lng - a.lng);
	const lat1 = toRadians(a.lat);
	const lat2 = toRadians(b.lat);

	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

	return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

// Courses without coordinates are dropped rather than treated as distance zero,
// which would make a broken row the permanent "nearest" club.
export function nearestUnplayed(courses, rounds, pos, index = 0) {
	const played = new Set((rounds || []).map((r) => r.courseId));

	const ranked = (courses || [])
		.filter(
			(c) =>
				c &&
				!played.has(c.id) &&
				Number.isFinite(c.latitude) &&
				Number.isFinite(c.longitude),
		)
		.map((c) => ({
			course: c,
			km: haversineKm(pos, { lat: c.latitude, lng: c.longitude }),
		}))
		.sort((a, b) => a.km - b.km);

	if (ranked.length === 0) return null;

	return ranked[index % ranked.length];
}

const MINUTE_MS = 60000;

// The Geolocation API may hand back an arbitrarily old fix and still report a
// tight accuracy, and `maximumAge` is a request the platform is free to ignore:
// on a ferry mid-fjord, macOS returned a 2h39m-old position at a claimed 35m
// accuracy even for `maximumAge: 0, enableHighAccuracy: true`. The timestamp is
// the only thing that gives it away. Returns a Norwegian warning, or null when
// the fix is recent enough to say nothing about.
export function stalePositionWarning(timestamp, now, maxAgeMs = 5 * MINUTE_MS) {
	const ageMs = now - timestamp;
	if (!(ageMs > maxAgeMs)) return null;

	const minutes = Math.round(ageMs / MINUTE_MS);
	if (minutes < 60) {
		return `Posisjonen er ${minutes} ${minutes === 1 ? "minutt" : "minutter"} gammel`;
	}

	const hours = Math.round(minutes / 60);
	return `Posisjonen er ${hours} ${hours === 1 ? "time" : "timer"} gammel`;
}
